import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ToastContainer, Slide, toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import Navbar from './components/Navbar';
import Breadcrumbs from './components/Breadcrumbs'

import * as Sentry from "@sentry/react";

import breadcrumbConfig from './helpers/breadcrumbConfig';
import WebSocketClient from "./services/WebSocketClient";

import Api from "./services/Api";
import NavMenu from "./components/NavMenu/NavMenu";
import { useAtom, useSetAtom } from 'jotai'
import { threadsAtom, pictureAtom, customerCategoryAtom, languageAtom, focusAreasAtom,
  unreadChatsAtom, departmentAtom, toastAtom, onboardedAtom, customerSurveysAtom } from './jotai_state';
import { Check as IconCheck } from "flowbite-react-icons/outline";
import iconToastCreated from "url:shared-assets/images/toast-created.svg";
import iconToastCompleted from "url:shared-assets/images/toast-completed.svg";
import { CONFIG } from "./constants";

// if not localhost
if (window.location.hostname !== "localhost") {
  Sentry.init({
    dsn: "https://3436c77ca5096a2502811a7920a68314@o4507441234575360.ingest.de.sentry.io/4507583744049232",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const App = () => {
  const api = Api.getInstance()
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const navbarRef = useRef(null);

  const [wide, setWide] = useState<boolean>(window.innerWidth > 924);
  const [debug, setDebug] = useState(false);
  const { i18n } = useTranslation();
  const webSocketClient = useRef(WebSocketClient.getInstance());
  const [expired, setExpired] = useState<string|null>(null);
  const setThreads = useSetAtom(threadsAtom);
  const setCustomerCategories = useSetAtom(customerCategoryAtom);
  const setFocusAreas = useSetAtom(focusAreasAtom);
  const setPicture = useSetAtom(pictureAtom);
  const setLang = useSetAtom(languageAtom);
  const setOnboarded = useSetAtom(onboardedAtom);
  const setCustomerSurveys = useSetAtom(customerSurveysAtom);

  const [globalToast, setGlobalToast] = useAtom(toastAtom);
  const [unreadChats, setUnreadChats] = useAtom(unreadChatsAtom);
  const setDepartment = useSetAtom(departmentAtom);

  const { isAuthenticated, isLoading } = useAuth0();

  const showMenu = !wide
  // Don't show menu if we show the feedback page to external users (?token=[external_token])
  const loggedIn = (!(location.pathname.startsWith('/feedback/survey') && location.search.startsWith('?token=')))

  const messageListener = (event: any) => {
    const mess = JSON.parse(event.data)
    if (!mess) return
    if (mess.type === 'unread_chats') {
      // This event comes from server counting number of unread chats
      setUnreadChats(mess.number)
    } else if (mess.type === 'meeting' && mess.badges.unread > 0) {
      // This event comes from when a client sends a chat to coach
      if (window.location.pathname.startsWith('/videocoach/chat/')) {
        const coachId = window.location.pathname.substring(17)
        api.get('client/read_messages/' + coachId).then(() => console.log('signalled read'))
        return
      }
      setUnreadChats(1)
    }
  }

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    if (!(location.pathname.startsWith('/feedback/questionnaire') && location.search.startsWith('?token='))) {
      // No need for web socket listener if external questionnaire request
      webSocketClient.current.webSocket?.addEventListener("message", messageListener)
    }

    api.get('user/start/' + CONFIG.USER_TYPE).then(response => {
      setThreads(response.json.threads);
      setUnreadChats(response.json.unreadChats);
      setCustomerCategories(response.json.customerCategories);
      setFocusAreas(response.json.focusAreas);
      setExpired(response.json.expired);
      setPicture(response.json.picture);
      setLang(response.json.language);
      setDebug(response.json.debug);
      setDepartment(response.json.department);
      setOnboarded(response.json.onboarded);
      setCustomerSurveys(response.json.customerSurveys);
      window.localStorage[CONFIG.PERSON_TOKEN] = response.json.token
      window.localStorage[CONFIG.USERNAME_TOKEN] = response.json.name
    });

    function setViewportHeight() {
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      setWide(window.innerWidth > 924)
    }
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);

    return () => {
      webSocketClient.current.webSocket?.removeEventListener("message", messageListener)
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (globalToast) {
      // https://www.npmjs.com/package/react-toastify
      const toastData = {
        position: globalToast.position ? globalToast.position : "top-right",
        theme: globalToast.theme ? globalToast.theme : "dark",
        closeOnClick: false,
        pauseOnFocusLoss: true,
        hideProgressBar: true,
        closeButton: false,
        autoClose: globalToast.autoClose ? globalToast.autoClose : 3000,
        transition: Slide,
        onClose: () => {setGlobalToast(null)}
      }
      if (globalToast.type === "error") {
        toast.error(globalToast.text, toastData);
      } else if (globalToast.type === "info") {
        toast.info(InfoToast, toastData);
      } else if (globalToast.type === "warning") {
        toast.warning(globalToast.text, toastData);
      } else {
        toast.success(globalToast.text, toastData);
      }
    }
  }, [globalToast])

  function InfoToast() {
    return (
      <div className="text-center" style={{minWidth: 300}}>
        <div className="mx-auto">
          <img src={globalToast?.infoType === "created" ? iconToastCreated : iconToastCompleted}
               alt="info" className="h-20 w-20 mx-auto"/>
          <div className="mt-4 z-title1">{globalToast?.text}</div>
        </div>
      </div>
    )
  }

  // useEffect(() => {
  //   window.HSStaticMethods.autoInit();
  // }, [location.pathname]);

  useEffect(() => {
    const isAuth = isAuthenticated && !isLoading;
    if (!isLoading) {
      // TODO: SHOULD BE DEPENDED ON THE LOCAL USER STATE
      if (isAuth && location.pathname === "/auth") {
        const redirectTo = searchParams.get('redirectTo');
        if (redirectTo) {
          navigate(redirectTo);
        }
      } else if (!isAuth) {

      }
    }
  }, [isLoading, isAuthenticated]);

  if (expired) {
    return (
      <div className="font-hoves mt-6 text-center">
        <p>
          {expired} — you’re currently unable to access the platform.
        </p>
        <p className="mt-4">
          Reach out to your manager or HR, or contact us at&nbsp;
          <a className="z-link" href="mailto:support@zebrain.se">support@zebrain.se</a>&nbsp;
          and we’ll help you get back on track.
        </p>
      </div>
    )
  }

  return (
    <div className="font-hoves">
      <div className="flex flex-col h-screen-custom">
        <div ref={navbarRef}>
          <Navbar
            showMenu={showMenu && loggedIn}
            loggedIn={loggedIn}
            i18n={i18n}
            badges={unreadChats > 0}
            debug={debug}
          />
        </div>

        <div className="flex">
          {
            (loggedIn && wide) &&
              <NavMenu showSidebar={true}/>
          }
          <div
            className={"z-main-scroll" + ((loggedIn && wide) ? " z-has-left-col ml-16" : "")}
            id="main_for_scrolling"
          >
            <Breadcrumbs breadcrumbConfig={breadcrumbConfig} />
            <Outlet />
          </div>
        </div>

      </div>
      <ToastContainer icon={({type}) => {
        switch (type) {
          case 'success':
            return <IconCheck className="size-6 text-success-500!" aria-hidden="true" />
          default:
            return null;
        }
      }}/>
    </div>
  );
}

export {App};
